import React, {useState, useRef, useCallback} from 'react';
import {View, StyleSheet, ScrollView, TouchableOpacity} from 'react-native';
import {Text, FAB} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import MaterialsTab from './MaterialsTab';
import ReadymadesTab from './ReadymadesTab';
import PurchasesTab from './PurchasesTab';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const TABS = [
  {key: 'materials', component: MaterialsTab},
  {key: 'readymades', component: ReadymadesTab},
  {key: 'purchases', component: PurchasesTab},
];

const TAB_LABEL_KEYS = {
  materials: 'inventory.tabMaterials',
  readymades: 'inventory.tabReadymades',
  purchases: 'inventory.tabPurchases',
};

const InventoryScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [activeTab, setActiveTab] = useState('materials');
  const scrollRef = useRef(null);
  const tabRefs = useRef({});
  const {selectedOrgUnitId} = useOrgUnitStore();

  const handleTabPress = useCallback((key) => {
    setActiveTab(key);
    const ref = tabRefs.current[key];
    if (ref && scrollRef.current) {
      ref.measureLayout(
        scrollRef.current.getInnerViewRef(),
        (x) => {
          scrollRef.current.scrollTo({x: Math.max(0, x - 20), animated: true});
        },
        () => {},
      );
    }
  }, []);

  const handleAdd = useCallback(() => {
    if (activeTab === 'materials') {
      navigation.navigate('AddEditMaterial', {defaultOrgUnitId: selectedOrgUnitId});
    } else if (activeTab === 'readymades') {
      navigation.navigate('AddEditReadymade', {defaultOrgUnitId: selectedOrgUnitId});
    }
  }, [activeTab, navigation, selectedOrgUnitId]);

  const ActiveComponent = TABS.find(tab => tab.key === activeTab)?.component;
  const showFab = activeTab !== 'purchases';

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('inventory.title')}
        onMenu={() => navigation.dispatch(DrawerActions.openDrawer())}>
        <OrgUnitSelector variant="light" />
      </GradientHeader>

      <View style={styles.tabBarContainer}>
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabBarContent}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                ref={ref => { tabRefs.current[tab.key] = ref; }}
                onPress={() => handleTabPress(tab.key)}
                style={[styles.tab, isActive && styles.tabActive]}
                activeOpacity={0.7}>
                <Text
                  style={[
                    styles.tabLabel,
                    isActive ? styles.tabLabelActive : styles.tabLabelInactive,
                  ]}>
                  {t(TAB_LABEL_KEYS[tab.key])}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      <View style={styles.content}>
        {ActiveComponent && <ActiveComponent selectedOrgUnitId={selectedOrgUnitId} />}
      </View>

      {showFab && (
        <FAB
          icon="plus"
          style={styles.fab}
          onPress={handleAdd}
          color={colors.white}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  tabBarContainer: {
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tabBarContent: {
    paddingHorizontal: spacing.xs,
  },
  tab: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: colors.primary,
  },
  tabLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  tabLabelActive: {
    color: colors.primary,
  },
  tabLabelInactive: {
    color: colors.textSecondary,
  },
  content: {flex: 1},
  fab: {
    position: 'absolute',
    margin: spacing.lg,
    right: 0,
    bottom: 0,
    backgroundColor: colors.primary,
  },
});

export default InventoryScreen;
